import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { PO_CATEGORY_LIST_PER_PAGE } from "../../Components/NewPurchaseOrder/constants";
import { mergePurchaseOrderCategoryOptions } from "../../Components/NewPurchaseOrder/apiUtils";
import { fetchPurchaseOrderCategories } from "../../services/purchaseOrders/purchaseOrdersApi";
import { normalizeCategoryListFilters, purchaseOrderKeys } from "./queryKeys";
import { poQueryCache } from "./cacheTimes";

/** Scroll-paginated category list for the product-picker filter. */
export function useNewPurchaseOrderCategoriesInfiniteQuery({
  merchantId,
  perPage = PO_CATEGORY_LIST_PER_PAGE,
  enabled = true,
}) {
  const filters = useMemo(
    () =>
      normalizeCategoryListFilters({
        merchantId,
        perPage,
      }),
    [merchantId, perPage]
  );

  const listQuery = useInfiniteQuery({
    queryKey: purchaseOrderKeys.categoryList(filters),
    queryFn: ({ pageParam, signal }) =>
      fetchPurchaseOrderCategories(
        {
          merchantId,
          page: pageParam,
          perPage,
        },
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.meta.hasMore) return undefined;
      if (allPages.length > 1) {
        const prevIds = new Set(
          allPages
            .slice(0, -1)
            .flatMap((page) => (page?.items ?? []).map((item) => String(item.id)))
        );
        const added = (lastPage.items ?? []).filter(
          (item) => !prevIds.has(String(item.id))
        );
        if (added.length === 0) return undefined;
      }
      return lastPage.meta.page + 1;
    },
    enabled: Boolean(enabled && merchantId),
    placeholderData: keepPreviousData,
    ...poQueryCache.vendorList,
  });

  const categories = useMemo(() => {
    const pages = listQuery.data?.pages;
    if (!Array.isArray(pages)) return [];
    return mergePurchaseOrderCategoryOptions(
      pages.flatMap((page) => page?.items ?? [])
    );
  }, [listQuery.data?.pages]);

  const loadMore = () => {
    if (!listQuery.hasNextPage || listQuery.isFetchingNextPage) return;
    listQuery.fetchNextPage();
  };

  return {
    categories,
    loadMore,
    hasMore: Boolean(listQuery.hasNextPage),
    isFetching: listQuery.isFetching,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    isPending: listQuery.isPending,
    error: listQuery.error,
    query: listQuery,
  };
}
